import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

import { OrgUnit, isIntake, parentOf, subtreeIds, unitPath, unitsUnder } from '../types';

import SettingsCard from './SettingsCard';
import {
  PanelCount,
  PanelCountGroup,
  PanelCountHeading,
  PanelCounts,
  PanelCountsEnd,
  PanelLabel,
  PanelPath,
  SectionDescription,
} from './styles';

export interface UnitPanelProps {
  units: OrgUnit[];
  unit: OrgUnit;
  onClose: () => void;
}

const peopleIn = (units: OrgUnit[], ids: string[]): number => {
  const wanted = new Set(ids);
  const walk = (list: OrgUnit[]): number =>
    list.reduce((total, unit) => total + (wanted.has(unit.uuid) ? unit.userCount : 0) + walk(unit.children), 0);
  return walk(units);
};

const CountList: React.FunctionComponent<{ heading: string; rows: [string, number][] }> = ({ heading, rows }) => (
  <PanelCountGroup aria-label={heading}>
    <PanelCountHeading>{heading}</PanelCountHeading>
    <PanelCounts>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <PanelLabel>{label}</PanelLabel>
          <PanelCount>{value}</PanelCount>
        </React.Fragment>
      ))}
    </PanelCounts>
  </PanelCountGroup>
);

const UnitPanel: React.FunctionComponent<UnitPanelProps> = ({ units, unit, onClose }) => {
  const isTop = !parentOf(units, unit.uuid);
  const beneath = subtreeIds(units, unit.uuid);
  const below = unitsUnder(units, unit.uuid);
  const directly = unit.children.filter((child) => !isIntake(child)).length;

  const renderCounts = (): React.ReactNode => {
    if (isIntake(unit)) {
      // Nothing is placed in a unit yet, so there is no count to show here until records carry one.
      return null;
    }
    return (
      <>
        <CountList
          heading="In this unit"
          rows={[
            ['Units directly under it', directly],
            ['People', unit.userCount],
          ]}
        />
        {below > 0 ? (
          <CountList
            heading="Including the units under it"
            rows={[
              ['Units', below],
              ['People', peopleIn(units, beneath)],
            ]}
          />
        ) : null}
      </>
    );
  };

  return (
    <SettingsCard
      as="aside"
      aria-label={`${unit.name} details`}
      data-testid="unit-panel"
      title={unit.name}
      action={
        <IconButton size="small" aria-label="Close details" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      {isTop ? null : <PanelPath>{unitPath(units, unit.uuid)}</PanelPath>}

      <PanelCountsEnd>{renderCounts()}</PanelCountsEnd>

      {isIntake(unit) ? (
        <SectionDescription>
          Records land here when nothing says which unit they belong to, such as an anonymous report that names no site.
          Each one waits here until it is moved to the right unit.
        </SectionDescription>
      ) : null}
    </SettingsCard>
  );
};

export default UnitPanel;
